"use client";

/**
 * TrackedWidget — automatic, sheet-aligned analytics for ONE page section,
 * added centrally by NewPage.renderComponent WITHOUT touching the widget itself.
 *
 * It provides both auto events:
 *   - widget_impression: fires via the existing 50%-visibility observer,
 *     observing the widget's OWN root element (see layout note).
 *   - cta_click context: exposes data-widget-name / data-widget-order so the
 *     single page-level <CtaClickDelegate/> can attribute any click inside it.
 *
 * The admin per-widget toggle flows in as `trackOn`: false → no observer +
 * data-track="off", so BOTH events are suppressed for this section.
 *
 * LAYOUT NOTE — the wrapper is `display: contents`, so it generates NO layout
 * box: the children participate in the parent's layout EXACTLY as if the wrapper
 * were not there. This makes wrapping layout-identical to no-wrapper on every
 * page — including the blog layout's flex/sticky article columns, where a real
 * <div> wrapper triggered a layout feedback loop that hung the browser. The
 * element still exists in the DOM, so `closest('[data-track]')` and the data
 * attributes work for the click delegate. Because the wrapper itself has no box,
 * the impression observer targets its first element child (the widget's root),
 * which does have a box.
 *
 * Part of the decoupled analytics feature — delete alongside rudderStackEvents.ts.
 */

import type { ReactNode } from "react";
import { useEffect, useRef } from "react";

import { trackWidgetImpression } from "@/customAnalytics/rudderStackEvents";

interface TrackedWidgetProps {
  /** Admin per-widget toggle. false → no impression + delegate skips its clicks. */
  trackOn: boolean;
  /** Resolved widget_name (admin name → section default). */
  widgetName: string;
  /** 1-based position of the section on the page. */
  widgetOrder: number | string;
  children: ReactNode;
}

export default function TrackedWidget({
  trackOn,
  widgetName,
  widgetOrder,
  children,
}: TrackedWidgetProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!trackOn) return;
    const wrapper = ref.current;
    if (!wrapper) return;

    let observedEl: Element | null = null;
    let cleanupImpression: (() => void) | null = null;

    // (Re)point the impression observer at the widget's CURRENT root element.
    const attachTo = (el: Element | null) => {
      if (el === observedEl) return;
      cleanupImpression?.();
      cleanupImpression = null;
      observedEl = el;
      if (el) {
        cleanupImpression = trackWidgetImpression(
          { current: el as HTMLElement },
          { widget_name: widgetName, widget_order: widgetOrder },
        );
      }
    };

    // The wrapper is display:contents (no box) — observe the widget's real root
    // element. Attach now if it already rendered…
    attachTo(wrapper.firstElementChild);

    // …and KEEP tracking the current root. Many widgets return null on their
    // FIRST client render (`if (isMobile === null) return null` — 11 widgets at
    // last count, e.g. WhatMakesCourseDifferent1 / About1 / Placements*): their
    // real <section> mounts a render AFTER this effect has run, so a one-shot
    // firstElementChild read observed nothing and those widgets NEVER fired a
    // widget_impression. The childList MutationObserver catches the root
    // appearing later (and a root being swapped) and re-attaches to it.
    const mo = new MutationObserver(() => attachTo(wrapper.firstElementChild));
    mo.observe(wrapper, { childList: true });

    return () => {
      mo.disconnect();
      cleanupImpression?.();
    };
  }, [trackOn, widgetName, widgetOrder]);

  return (
    <div
      ref={ref}
      style={{ display: "contents" }}
      data-track={trackOn ? "on" : "off"}
      data-widget-name={trackOn ? widgetName : undefined}
      data-widget-order={trackOn ? String(widgetOrder) : undefined}
    >
      {children}
    </div>
  );
}
